import React, { useCallback, useRef } from "react";
import {
  HiOutlineChatAlt2,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  HiOutlinePlus,
  HiOutlineSparkles,
} from "react-icons/hi";
import FloatingPopover, {
  FLOATING_POPOVER_MODE,
  FLOATING_POPOVER_PLACEMENT,
  FLOATING_POPOVER_VARIANT,
} from "components/common/FloatingPopover";
import CustomButton from "components/common/Button/CustomButton";
import { classNames } from "Utils/utils";
import {
  VERTEX360_COPILOT_SIDEBAR_ITEM_ACTIVE_CLASS,
  VERTEX360_COPILOT_SIDEBAR_ITEM_CLASS,
  VERTEX360_COPILOT_SIDEBAR_WIDTH_COLLAPSED,
  VERTEX360_COPILOT_SIDEBAR_WIDTH_EXPANDED,
  VERTEX360_COPILOT_WORKSPACE_VIEWS,
} from "../constants/vertex360CopilotWorkspaceConstants";
import { useVertex360CopilotWorkspace } from "../hooks/useVertex360CopilotWorkspace";
import Vertex360CopilotConversationRow from "./Vertex360CopilotConversationRow";
import { Vertex360CopilotSidebarConversationsSkeleton } from "./Vertex360CopilotWorkspaceSkeleton";

const SCROLL_LOAD_THRESHOLD_PX = 56;

const LoadingChatsFooter = ({ visible }) => {
  if (!visible) return null;
  return (
    <div
      className="flex items-center justify-center gap-2 px-2 py-3 text-[11px] font-medium text-slate-500"
      aria-live="polite"
      aria-busy="true"
    >
      <span
        className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-[#4B6CB7]"
        aria-hidden="true"
      />
      <span>Loading chats…</span>
    </div>
  );
};

const SidebarSection = ({
  title,
  children,
  className = "",
  onNearEnd,
  isLoadingMore = false,
}) => {
  const onScroll = useCallback(
    (event) => {
      if (!onNearEnd) return;
      const el = event.currentTarget;
      if (!el) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining <= SCROLL_LOAD_THRESHOLD_PX) {
        onNearEnd();
      }
    },
    [onNearEnd]
  );

  return (
    <div className={classNames("flex min-h-0 flex-col", className)}>
      {title ? (
        <h3 className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {title}
        </h3>
      ) : null}
      <div
        onScroll={onScroll}
        className="v360-panel-scroll-gray min-h-0 flex-1 overflow-y-auto pr-1"
      >
        {children}
        <LoadingChatsFooter visible={isLoadingMore} />
      </div>
    </div>
  );
};

const CollapsedNavButton = ({ label, icon, active, onClick, buttonRef }) => {
  const localRef = useRef(null);
  const ref = buttonRef || localRef;

  return (
    <FloatingPopover
      mode={FLOATING_POPOVER_MODE.TOOLTIP}
      variant={FLOATING_POPOVER_VARIANT.BRAND}
      placement={FLOATING_POPOVER_PLACEMENT.RIGHT}
      anchorRef={ref}
      maxWidth={220}
      offset={12}
      seamlessArrow
      preventFlip
      contentClassName="!p-0"
      content={
        <p className="whitespace-nowrap px-3 py-2 text-xs font-semibold text-slate-700">
          {label}
        </p>
      }
    >
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={classNames(
          "inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors",
          VERTEX360_COPILOT_SIDEBAR_ITEM_CLASS,
          active
            ? classNames(VERTEX360_COPILOT_SIDEBAR_ITEM_ACTIVE_CLASS, "text-[#4B6CB7]")
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        )}
        aria-label={label}
      >
        {icon}
      </button>
    </FloatingPopover>
  );
};

const Vertex360CopilotSidebar = () => {
  const collapseRef = useRef(null);
  const newChatRef = useRef(null);
  const chatsNavRef = useRef(null);

  const {
    sidebarCollapsed,
    view,
    activeConversationId,
    starredConversations,
    recentConversations,
    isLoadingConversations,
    starredSection,
    recentSection,
    loadMoreStarredConversations,
    loadMoreRecentConversations,
    toggleSidebar,
    startNewChat,
    setView,
    selectConversation,
    starConversation,
    unstarConversation,
    renameConversation,
    requestDeleteConversation,
  } = useVertex360CopilotWorkspace();

  const isChatsView = view === VERTEX360_COPILOT_WORKSPACE_VIEWS.MANAGE;
  const isStarredInitialLoading =
    Boolean(starredSection?.isLoading || isLoadingConversations) &&
    starredConversations.length === 0 &&
    !starredSection?.loaded;
  const showStarredSection =
    isStarredInitialLoading ||
    starredSection?.isLoadingMore ||
    starredConversations.length > 0;

  const rowHandlers = {
    onOpen: selectConversation,
    onStar: starConversation,
    onUnstar: unstarConversation,
    onRename: renameConversation,
    onDelete: requestDeleteConversation,
  };

  if (sidebarCollapsed) {
    return (
      <aside
        className="copilot-workspace-sidebar copilot-workspace-sidebar--collapsed flex shrink-0 flex-col items-center gap-2 border-r border-slate-200/80 bg-[#FAFBFD] py-3"
        style={{ width: VERTEX360_COPILOT_SIDEBAR_WIDTH_COLLAPSED }}
      >
        <CollapsedNavButton
          label="Expand sidebar"
          icon={<HiOutlineChevronDoubleRight className="h-5 w-5" />}
          onClick={toggleSidebar}
          buttonRef={collapseRef}
        />
        <CollapsedNavButton
          label="New chat"
          icon={<HiOutlinePlus className="h-5 w-5" />}
          onClick={startNewChat}
          buttonRef={newChatRef}
        />
        <CollapsedNavButton
          label="Chats"
          icon={<HiOutlineChatAlt2 className="h-5 w-5" />}
          active={isChatsView}
          onClick={() => setView(VERTEX360_COPILOT_WORKSPACE_VIEWS.MANAGE)}
          buttonRef={chatsNavRef}
        />
      </aside>
    );
  }

  return (
    <aside
      className="copilot-workspace-sidebar flex shrink-0 flex-col border-r border-slate-200/80 bg-[#FAFBFD]"
      style={{ width: VERTEX360_COPILOT_SIDEBAR_WIDTH_EXPANDED }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-slate-200/70 px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] text-white shadow-md shadow-[#4B6CB7]/20">
            <HiOutlineSparkles className="h-4 w-4" />
          </span>
          <span className="truncate text-sm font-semibold text-slate-800">Copilot</span>
        </div>
        <button
          ref={collapseRef}
          type="button"
          onClick={toggleSidebar}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#4B6CB7]/40"
          aria-label="Collapse sidebar"
        >
          <HiOutlineChevronDoubleLeft className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-2 px-2 py-3">
        <CustomButton
          type="button"
          variant="primary"
          label="New chat"
          Icon={<HiOutlinePlus className="h-4 w-4" />}
          clickHandler={startNewChat}
          cssClass="w-full !justify-center !py-2.5"
        />

        <button
          type="button"
          onClick={() => setView(VERTEX360_COPILOT_WORKSPACE_VIEWS.MANAGE)}
          className={classNames(
            "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
            VERTEX360_COPILOT_SIDEBAR_ITEM_CLASS,
            isChatsView
              ? VERTEX360_COPILOT_SIDEBAR_ITEM_ACTIVE_CLASS
              : "text-slate-600 hover:bg-slate-100"
          )}
        >
          <HiOutlineChatAlt2 className="h-4 w-4 shrink-0" />
          Chats
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-1 pb-3">
        {showStarredSection ? (
          <SidebarSection
            title="Starred"
            className={classNames(
              starredConversations.length > 6 ? "max-h-[40%]" : "shrink-0 max-h-[40%]"
            )}
            onNearEnd={loadMoreStarredConversations}
            isLoadingMore={Boolean(starredSection?.isLoadingMore)}
          >
            {isStarredInitialLoading ? (
              <div aria-busy="true" aria-label="Loading starred chats">
                <Vertex360CopilotSidebarConversationsSkeleton
                  rows={3}
                  showActive={false}
                />
              </div>
            ) : (
              <div className="space-y-0.5">
                {starredConversations.map((conversation) => (
                  <Vertex360CopilotConversationRow
                    key={conversation.id}
                    conversation={conversation}
                    isActive={
                      !isChatsView && activeConversationId === conversation.id
                    }
                    {...rowHandlers}
                  />
                ))}
              </div>
            )}
          </SidebarSection>
        ) : null}

        <SidebarSection
          title="Recent chats"
          className="min-h-0 flex-1"
          onNearEnd={loadMoreRecentConversations}
          isLoadingMore={Boolean(recentSection?.isLoadingMore)}
        >
          {isLoadingConversations && recentConversations.length === 0 ? (
            <div aria-busy="true" aria-label="Loading recent chats">
              <Vertex360CopilotSidebarConversationsSkeleton />
            </div>
          ) : recentConversations.length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-slate-400">
              No recent chats yet. Start a new conversation.
            </p>
          ) : (
            <div className="space-y-0.5">
              {recentConversations.map((conversation) => (
                <Vertex360CopilotConversationRow
                  key={conversation.id}
                  conversation={conversation}
                  isActive={
                    !isChatsView && activeConversationId === conversation.id
                  }
                  {...rowHandlers}
                />
              ))}
            </div>
          )}
        </SidebarSection>
      </div>
    </aside>
  );
};

export default Vertex360CopilotSidebar;
